import { validate as isUuid } from 'uuid';

import { FE_SESSION_ID_KEY } from 'constants/session';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from 'utils/storage';

type FrontendSessionChangeListener = (sessionId: string | null) => void;

const frontendSessionChangeListeners = new Set<FrontendSessionChangeListener>();

function notifyFrontendSessionChanged(sessionId: string | null): void {
    frontendSessionChangeListeners.forEach((listener) => listener(sessionId));
}

export function getValidFrontendSessionId(): string | null {
    const sessionId = getLocalStorageItem(FE_SESSION_ID_KEY);
    if (!sessionId) {
        return null;
    }
    if (isUuid(sessionId)) {
        return sessionId;
    }

    clearFrontendSessionId();
    return null;
}

export function storeFrontendSessionId(sessionId: unknown): void {
    if (typeof sessionId !== 'string' || !isUuid(sessionId)) {
        clearFrontendSessionId();
        return;
    }

    const previousSessionId = getLocalStorageItem(FE_SESSION_ID_KEY);
    setLocalStorageItem(FE_SESSION_ID_KEY, sessionId);
    if (previousSessionId !== sessionId && getLocalStorageItem(FE_SESSION_ID_KEY) === sessionId) {
        notifyFrontendSessionChanged(sessionId);
    }
}

export function clearFrontendSessionId(): void {
    const previousSessionId = getLocalStorageItem(FE_SESSION_ID_KEY);
    removeLocalStorageItem(FE_SESSION_ID_KEY);
    if (previousSessionId !== null && getLocalStorageItem(FE_SESSION_ID_KEY) === null) {
        notifyFrontendSessionChanged(null);
    }
}

export function subscribeToFrontendSessionChanges(listener: FrontendSessionChangeListener): () => void {
    frontendSessionChangeListeners.add(listener);

    const handleStorage = (event: StorageEvent): void => {
        if (event.key !== FE_SESSION_ID_KEY || event.oldValue === event.newValue) return;
        listener(event.newValue && isUuid(event.newValue) ? event.newValue : null);
    };
    window.addEventListener('storage', handleStorage);

    return () => {
        frontendSessionChangeListeners.delete(listener);
        window.removeEventListener('storage', handleStorage);
    };
}
